import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import AdminLayout from "@/components/AdminLayout";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ArrowLeft, RefreshCw, ShieldCheck, ShieldOff, Lock, Ban, ShieldX, Star } from "lucide-react";

const TIER = {
  Low: "bg-ec-emerald/10 text-ec-emerald border-ec-emerald/40",
  Medium: "bg-amber-500/10 text-amber-400 border-amber-500/40",
  High: "bg-orange-500/10 text-orange-400 border-orange-500/40",
  Critical: "bg-ec-error/20 text-ec-error border-ec-error/40",
};

export default function TrustSafetyAccount() {
  const { uid } = useParams();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get(`/admin/trust-safety/account/${uid}`);
      setData(data);
    } catch (e) {}
  };
  useEffect(() => { load(); }, [uid]);

  const rescan = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/trust-safety/scan/${uid}`);
      toast.success("Re-scanned");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  if (!data) return <AdminLayout title="Loading…" subtitle="Trust & Safety" />;

  const p = data.profile;
  const u = data.user;
  const tierCls = TIER[p.risk_level] || TIER.Low;

  return (
    <AdminLayout
      subtitle="Trust & Safety"
      title="Account Investigation"
      actions={
        <div className="flex items-center gap-2">
          <Link to="/admin/trust-safety/alerts"><Button size="sm" variant="outline" className="rounded-sm border-white/10 text-white gap-2 hover:bg-white/10" data-testid="ts-account-back"><ArrowLeft className="w-3 h-3" />Back</Button></Link>
          <Button size="sm" onClick={rescan} disabled={busy} className="rounded-sm bg-ec-emerald hover:bg-ec-emerald/90 text-ec-ink gap-2" data-testid="ts-account-rescan">
            <RefreshCw className={`w-3 h-3 ${busy ? "animate-spin" : ""}`} />Re-scan
          </Button>
        </div>
      }
    >
      {/* Identity + score */}
      <Card className="rounded-sm border-white/10 bg-white/[0.02] mb-6">
        <CardContent className="p-6 grid md:grid-cols-[1fr_220px] gap-6 items-start">
          <div>
            <div className="flex items-center gap-3 flex-wrap mb-1">
              <Badge className="bg-white/10 border-white/10 rounded-sm">{u.role}</Badge>
              {u.suspended && <Badge className="bg-ec-error/20 text-ec-error border-ec-error/40 rounded-sm">Suspended</Badge>}
              {u.trusted && <Badge className="bg-ec-emerald/10 text-ec-emerald border-ec-emerald/40 rounded-sm gap-1"><Star className="w-3 h-3" />Trusted</Badge>}
              {p.whitelisted && <Badge className="bg-ec-emerald/10 text-ec-emerald border-ec-emerald/40 rounded-sm gap-1"><ShieldCheck className="w-3 h-3" />Whitelisted</Badge>}
              {u.restrict_certificates && <Badge className="bg-amber-500/10 text-amber-400 border-amber-500/40 rounded-sm">Certs restricted</Badge>}
              {u.restrict_recommendations && <Badge className="bg-amber-500/10 text-amber-400 border-amber-500/40 rounded-sm">Recs restricted</Badge>}
              {u.verification_frozen && <Badge className="bg-orange-500/10 text-orange-400 border-orange-500/40 rounded-sm">Verification frozen</Badge>}
            </div>
            <h2 className="font-display text-3xl font-black text-white" data-testid="ts-account-name">{u.name}</h2>
            <div className="text-sm text-zinc-300 mt-1">{u.email}</div>
            <div className="text-xs font-mono-plex text-zinc-400 mt-0.5">{u.ethic_id}</div>
          </div>
          <div className="text-center" data-testid="ts-account-score">
            <div className="eyebrow text-zinc-500">Fraud score</div>
            <div className="font-display text-6xl font-black mt-1">{p.score}</div>
            <div className="text-xs font-mono-plex text-zinc-500 mt-1">/ 100</div>
            <Badge className={`mt-3 ${tierCls} border rounded-sm`}>{p.risk_level}</Badge>
          </div>
        </CardContent>
      </Card>

      {/* Open signals */}
      <Card className="rounded-sm border-white/10 bg-white/[0.02] mb-6">
        <CardContent className="p-6">
          <div className="eyebrow text-ec-emerald mb-3">Signals detected · {p.signals?.length || 0}</div>
          {(!p.signals || p.signals.length === 0) ? (
            <div className="text-sm text-zinc-500" data-testid="ts-account-no-signals">No fraud signals on this account.</div>
          ) : (
            <ul className="space-y-3" data-testid="ts-account-signals">
              {p.signals.map((s, i) => (
                <li key={i} className="bg-black/30 border border-white/10 p-4 rounded-sm" data-testid={`ts-signal-${s.detector_key}`}>
                  <div className="flex items-center gap-2 flex-wrap mb-2">
                    <Badge className={`${TIER[s.risk_level] || TIER.Low} border rounded-sm`}>{s.risk_level}</Badge>
                    <Badge className="bg-white/10 border-white/10 rounded-sm font-mono-plex text-[10px]">{s.detector_key}</Badge>
                    <Badge className="bg-white/10 border-white/10 rounded-sm">+{s.score} pts</Badge>
                    <span className="text-[10px] font-mono-plex text-zinc-500 ml-auto">suggested · {s.suggested_action.replace(/_/g, " ")}</span>
                  </div>
                  <p className="text-sm text-zinc-200">{s.reason}</p>
                  {s.evidence && Object.keys(s.evidence).length > 0 && (
                    <div className="text-xs font-mono-plex text-zinc-500 mt-2">
                      {Object.entries(s.evidence).map(([k, v]) => `${k}: ${v}`).join("  ·  ")}
                    </div>
                  )}
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      {/* Investigation log */}
      <Card className="rounded-sm border-white/10 bg-white/[0.02]">
        <CardContent className="p-6">
          <div className="eyebrow text-ec-emerald mb-3">Investigation log · {data.investigations.length}</div>
          {data.investigations.length === 0 ? (
            <div className="text-sm text-zinc-500" data-testid="ts-account-no-history">No prior enforcement actions on this account.</div>
          ) : (
            <ul className="space-y-2 text-sm" data-testid="ts-account-history">
              {data.investigations.map((i) => (
                <li key={i.id} className="flex items-start gap-3 py-2 border-b border-white/5 last:border-0">
                  <div className="font-mono-plex text-[10px] text-zinc-500 w-32">{i.created_at?.slice(0, 16)}</div>
                  <div className="flex-1">
                    <Badge className="bg-white/10 border-white/10 rounded-sm font-mono-plex text-[10px] mr-2">{i.action}</Badge>
                    <span className="text-xs text-zinc-400">{i.detector_key}</span>
                    {i.note && <div className="text-xs text-zinc-300 mt-1">{i.note}</div>}
                    <div className="text-[10px] font-mono-plex text-zinc-600 mt-1">by {i.admin_email}</div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </AdminLayout>
  );
}
